import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import CreateAdminModal from './modals/CreateAdminModal'
import EditAdminModal from './modals/EditAdminModal'
import { adminsApi } from '@/api/endpoints/admins'
import { parishesApi } from '@/api/endpoints/parishes'
import { openEmailClient, getAdminLoginEmailTemplate } from '@/utils/email'
import { useUI } from '@/stores/ui'
import Spinner from '@/components/common/Spinner'

interface AdminUser {
  id: string
  first_name?: string
  last_name?: string
  email: string
  can_manage_admins?: boolean
  remind_password_change_90_days?: boolean
  password?: string // Password is not returned from API, but we track it locally if available
}

export default function AdminList({ open = false, onToggle }: { open?: boolean; onToggle?: () => void }) {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [showPasswords, setShowPasswords] = useState(false)
  const [globalRemindPasswordChange, setGlobalRemindPasswordChange] = useState(false)
  const [parishName, setParishName] = useState('')
  const [passwordVisible, setPasswordVisible] = useState<Set<string>>(new Set())

  // Load admins, pending admins, and parish name
  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!user?.parish_id) return
      setLoading(true)
      setError(null)
      try {
        const [adminsData, parishesData] = await Promise.all([
          adminsApi.list(),
          parishesApi.list(),
        ])
        if (mounted) {
          const adminsList = Array.isArray(adminsData) ? (adminsData as AdminUser[]) : []
          setAdmins(adminsList)
          
          // Set global reminder checkbox based on whether all admins have it enabled
          // If all admins have it enabled, check the box; otherwise uncheck
          if (adminsList.length > 0) {
            const allEnabled = adminsList.every(a => a.remind_password_change_90_days === true)
            setGlobalRemindPasswordChange(allEnabled)
          }
          
          const activeParish = Array.isArray(parishesData) ? parishesData.find(p => p.id === user.parish_id) : null
          if (activeParish) setParishName(activeParish.name)
          
        }
      } catch {
        if (mounted) setAdmins([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [user?.parish_id, user?.role])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return admins
    return admins.filter(a =>
      `${a.first_name || ''} ${a.last_name || ''} ${a.email}`.toLowerCase().includes(q)
    )
  }, [admins, query])

  const handleSendLoginEmail = (admin: AdminUser) => {
    const loginUrl = window.location.origin || 'https://www.catechismcloud.com'
    
    const template = getAdminLoginEmailTemplate({
      adminFirstName: admin.first_name || 'Admin',
      adminEmail: admin.email,
      adminPassword: admin.password, // May be undefined if not available
      parishName: parishName || 'Parish',
      senderTitle: user?.title || undefined,
      senderLastName: user?.last_name || 'Administrator',
      loginUrl,
    })

    openEmailClient(admin.email, template)
    addToast({
      message: `Email client opened with login credentials for ${admin.first_name || admin.email}`,
      type: 'success',
    })
  }

  return (
    <motion.div
      className="mt-8 p-5 rounded-xl border border-gray-200 bg-white shadow-sm"
      aria-busy={loading}
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700 focus-ring rounded px-1 -ml-1">
          <span className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▸</span>
          Admins
        </button>
        <div className="flex items-center gap-2">
          <input
            className="input-field w-64 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
            placeholder="Search admins…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search admins"
          />
          <button className="btn-primary text-sm transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={() => setShowCreate(true)} disabled={loading} aria-disabled={loading}>
            Add Admin
          </button>
        </div>
      </div>
      
      {open && (<>

      {/* Password visibility toggle and reminder notice */}
      <div className="mb-4 flex items-start justify-between gap-4">
        {/* Red alert box - only extends to end of text, not full width */}
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg inline-block">
          <div className="flex items-start gap-2">
            <span className="text-red-600 font-bold text-sm">⚠️ Important:</span>
            <div>
              <p className="text-xs text-red-700 mb-2">
                It is highly recommended that you reset Admin passwords at least every 90 days!
              </p>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  className="w-3 h-3 text-yellow-500 focus-ring rounded border-gray-300"
                  checked={globalRemindPasswordChange}
                  onChange={async (e) => {
                    const newValue = e.target.checked
                    setGlobalRemindPasswordChange(newValue)
                    try {
                      await adminsApi.updatePasswordReminderForAll(newValue)
                      // Update local state for all admins
                      setAdmins(prev => prev.map(admin => ({
                        ...admin,
                        remind_password_change_90_days: newValue
                      })))
                      addToast({
                        message: `Password reminder ${newValue ? 'enabled' : 'disabled'} for all admins`,
                        type: 'success',
                      })
                    } catch (error: any) {
                      // Revert checkbox on error
                      setGlobalRemindPasswordChange(!newValue)
                      addToast({
                        message: error.response?.data?.detail || 'Failed to update password reminder setting',
                        type: 'error',
                      })
                    }
                  }}
                />
                <span className="text-xs text-gray-700">Automatically remind Admins to change their passwords every 90 days</span>
              </label>
            </div>
          </div>
        </div>
        {/* Show passwords checkbox - separate, on the far right */}
        <div className="flex-shrink-0 pt-3">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              className="w-3 h-3 text-yellow-500 focus-ring rounded border-gray-300"
              checked={showPasswords}
              onChange={(e) => setShowPasswords(e.target.checked)}
            />
            <span className="text-xs text-gray-700">Show passwords</span>
          </label>
        </div>
      </div>

      {loading && <Spinner label="Loading admins…" />}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Admin Details</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Role</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Password</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-4 text-sm text-gray-500 text-center">No admins found.</td>
                </tr>
              ) : (
                filtered.map(a => {
                  // Use the admin's own role (was: "is it me?"), so any viewer sees who the Primary Admin is
                  const isPrimaryAdmin = (a as any).role === 'PRIMARY_ADMIN'
                  const isDesignated = (a as any).role === 'DESIGNATED_ADMIN' || !!a.can_manage_admins
                  const roleLabel = isPrimaryAdmin ? 'Primary Admin' : (isDesignated ? 'Designated Admin' : 'Admin')
                  const isPasswordVisible = passwordVisible.has(a.id)
                  
                  return (
                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <div className="font-medium">{a.first_name} {a.last_name}</div>
                        <div className="text-xs text-gray-600">{a.email}</div>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          isPrimaryAdmin 
                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                            : isDesignated
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}>
                          {roleLabel}
                        </span>
                      </td>
                      <td className="p-3">
                        {showPasswords && a.password ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-gray-700">
                              {isPasswordVisible ? a.password : '••••••••••'}
                            </span>
                            <button
                              type="button"
                              className="text-xs text-gray-600 hover:text-gray-800 focus-ring"
                              onClick={() => {
                                const newSet = new Set(passwordVisible)
                                if (isPasswordVisible) {
                                  newSet.delete(a.id)
                                } else {
                                  newSet.add(a.id)
                                }
                                setPasswordVisible(newSet)
                              }}
                              title={isPasswordVisible ? 'Hide password' : 'Show password'}
                            >
                              {isPasswordVisible ? '👁️' : '👁️‍🗨️'}
                            </button>
                            <button
                              type="button"
                              className="text-xs text-gray-600 hover:text-gray-800 focus-ring"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(a.password || '')
                                  addToast({ message: 'Password copied to clipboard', type: 'success' })
                                } catch {
                                  addToast({ message: 'Failed to copy password', type: 'error' })
                                }
                              }}
                              title="Copy password"
                            >
                              📋
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">(Not available - use Send Login to email new password)</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <button
                            className="btn-secondary text-xs focus-ring"
                            onClick={() => handleSendLoginEmail(a)}
                            title={`Open email client with pre-filled login credentials for ${a.first_name || a.email}`}
                            aria-label={`Send login email to ${a.first_name || a.email}`}
                          >
                            Send password
                          </button>
                          <button 
                            className="btn-secondary text-xs focus-ring"
                            onClick={() => setEditing(a)}
                            aria-label={`Edit ${a.first_name || a.email}`}
                          >
                            Edit details
                          </button>
                          {!isPrimaryAdmin && (
                            <button
                              className="text-xs text-red-600 hover:text-red-800 focus-ring p-1"
                              onClick={async () => {
                                if (!confirm(`Are you sure you want to delete ${a.first_name || a.email}?`)) return
                                try {
                                  await adminsApi.remove(a.id)
                                  setAdmins(prev => prev.filter(admin => admin.id !== a.id))
                                  addToast({ message: 'Admin deleted', type: 'success' })
                                } catch (error: any) {
                                  addToast({
                                    message: error.response?.data?.detail || 'Failed to delete admin',
                                    type: 'error',
                                  })
                                }
                              }}
                              title="Delete admin"
                              aria-label={`Delete ${a.first_name || a.email}`}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      </>)}
      <CreateAdminModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={async (newAdmin?: { email: string; password?: string }) => {
          // Refresh the admin list
          let adminsList: AdminUser[] = []
          try {
            const adminsData = await adminsApi.list()
            adminsList = Array.isArray(adminsData) ? (adminsData as AdminUser[]) : []
            setAdmins(adminsList)
            
            // Update global reminder checkbox
            if (adminsList.length > 0) {
              const allEnabled = adminsList.every((a: AdminUser) => a.remind_password_change_90_days === true)
              setGlobalRemindPasswordChange(allEnabled)
            }
          } catch (error) {
            console.error('Failed to refresh admin list:', error)
          }
          
          // If we have password from creation, preserve it temporarily for email sending
          if (newAdmin?.password && newAdmin?.email && adminsList.length > 0) {
            const adminWithPassword = adminsList.find((a: AdminUser) => a.email === newAdmin.email)
            if (adminWithPassword) {
              adminWithPassword.password = newAdmin.password
              // Automatically open email client
              setTimeout(() => {
                handleSendLoginEmail(adminWithPassword)
              }, 500)
            }
          }
        }}
      />
      {editing && (
        <EditAdminModal
          open={!!editing}
          onClose={() => setEditing(null)}
          admin={editing}
          onSaved={(updated) => {
            // EditAdminModal already persisted the change; just reflect it in the list.
            setAdmins((prev) => prev.map(p => p.id === updated.id ? (updated as AdminUser) : p))
          }}
        />
      )}
    </motion.div>
  )
}


